import { apiSlice } from '../../apis/api';
import { AsoCollection } from '../../swagger/collections';
import {
  AsoScenarioCreateRequest,
  AsoRequestCreateRequest,
  AsoRequestResponse,
  AsoRequestUpdateRequest,
  AsoScenarioResponse,
  AsoScenarioUpdateRequest,
  ApiOperationType,
  AsoScenarioScopeResponse,
  AsoScenarioScopeSourceResponse,
  AsoScenarioScopeUpdateRequest,
  AsoScenarioScopeSourceUpdateRequest,
  AsoScenarioScopeTargetResponse,
  AsoScenarioScopeTargetUpdateRequest,
  AsoScenarioMinLayerResponse,
  AsoScenarioMinLayerCreateRequest,
  AsoScenarioMinLayerUpdateRequest,
  AsoScenarioStoreTierSettingsResponse,
  AsoScenarioStoreTierSettingsUpdateRequest,
  AsoScenarioStoreTierResponse,
  AsoScenarioStoreTierUpdateRequest,
  AsoScenarioStoreTierForceRequest,
  AsoScenarioConfiguration,
  AsoScenarioStoreTierConfigurationPatchRequest,
  AsoScenarioStoreTierConfigurationResponse,
  AsoScenarioStoreTierType,
  AsoScenarioConfigurationPatchRequest,
  AsoScenarioUseSets,
  AsoScenarioSpecialPeriodMode,
  AsoScenarioAdditionalOneStepAdjustment,
  AsoScenarioStatus,
} from '@ydistri/api-sdk';
import { getTags } from '../../apis/apiLib';
import { CreateAsoParams } from './asoLib';
import { signalrClient } from '../../signalr/client';
import {
  SignalrUpdate,
  SignalAsoConfigurationChanged,
  SignalAsoScenarioChanged,
} from '../../signalr/signalrInterfaces';
import { isEnumValue } from '@ydistri/generic-utils';
import {
  handleAsoScenarioScopeChangeListener,
  removeAsoScope,
  removeAsoScopeTarget,
  setAsoScopeSource,
  upsertAsoScope,
  upsertAsoScopeTarget,
} from './asoScenarioScopeChangeListener';
import { handleAsoScenarioLayerChangeListener } from './asoScenarioLayerChangeListener';
import { runVoid } from '../CalculationDetail/Editation/apiCalculationEdit';
import { getSelectedProjectShortName } from '../../apis/apiHeader';

const { TAGS, TAGS_ARRAY } = getTags('aso');
const { TAGS: SCOPE_TAGS } = getTags('scopesAdministration');

const getScopeUsageTags = (...scopeIds: Array<number | null | undefined>) =>
  [
    ...new Set(
      scopeIds.filter((scopeId): scopeId is number => scopeId !== null && scopeId !== undefined),
    ),
  ].map(scopeId => ({ type: SCOPE_TAGS.scopeUsages, id: scopeId }));

const normalizeAsoScenarioFieldName = (
  fieldName: string,
): keyof Pick<AsoScenarioResponse, 'name' | 'status'> | undefined => {
  switch (fieldName.toLowerCase()) {
    case 'name':
      return 'name';
    case 'status':
      return 'status';
    default:
      return undefined;
  }
};

const parseAsoScenarioStatus = (value?: string): AsoScenarioStatus | undefined => {
  if (value === undefined) return undefined;
  return isEnumValue(AsoScenarioStatus, value) ? value : undefined;
};

const parseNullableNumber = (value?: string): number | null => {
  if (value === undefined || value.trim() === '') return null;

  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const applyAsoScenarioStoreTierConfigurationUpdate = (
  row: AsoScenarioStoreTierConfigurationResponse,
  update: SignalrUpdate,
) => {
  switch (update.fieldName) {
    case 'tierMultiplier':
      row.tierMultiplier = parseNullableNumber(update.value);
      break;
    case 'balancingMinimumInventoryMonths':
      row.balancingMinimumInventoryMonths = parseNullableNumber(update.value);
      break;
    case 'balancingMedianInventoryMonths':
      row.balancingMedianInventoryMonths = parseNullableNumber(update.value);
      break;
    case 'balancingMaximumInventoryMonths':
      row.balancingMaximumInventoryMonths = parseNullableNumber(update.value);
      break;
  }
};

export const apiAso = apiSlice.enhanceEndpoints({ addTagTypes: TAGS_ARRAY }).injectEndpoints({
  endpoints: builder => ({
    // Requests
    createAsoRequest: builder.mutation<AsoRequestResponse, AsoRequestCreateRequest>({
      queryFn: async payload => {
        const { data } = await AsoCollection.requestsCreate(payload);
        return { data: data.data };
      },
      invalidatesTags: [TAGS.requests],
    }),

    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- void
    getAsoRequests: builder.query<AsoRequestResponse[], void>({
      queryFn: async () => {
        const { data } = await AsoCollection.getAsoRequests();
        return { data: data.data };
      },
      providesTags: [TAGS.requests],
    }),

    updateAsoRequest: builder.mutation<
      AsoRequestResponse,
      { id: number; payload: AsoRequestUpdateRequest }
    >({
      queryFn: async ({ id, payload }) => {
        const { data } = await AsoCollection.requestsPartialUpdate(id, payload);
        return { data: data.data };
      },
      invalidatesTags: [TAGS.requests],
    }),

    deleteAsoRequest: builder.mutation<unknown, number>({
      queryFn: async id => {
        const { data } = await AsoCollection.requestsDelete(id);
        return { data };
      },
      invalidatesTags: [TAGS.requests],
    }),

    // ASO root list/detail
    getAsoScenarioList: builder.query<AsoScenarioResponse[], undefined>({
      queryFn: async () => {
        const { data } = await AsoCollection.scenariosList();
        return { data: data.data };
      },
      providesTags: [TAGS.list],
      async onCacheEntryAdded(unusedQueryArg, api) {
        try {
          await api.cacheDataLoaded;

          const listener = (signal: SignalAsoConfigurationChanged) => {
            if (
              signal.entityType !== 'AsoScenario' ||
              signal.operationType !== ApiOperationType.Patch
            ) {
              return;
            }

            api.updateCachedData(draft => {
              const scenario = draft.find(item => item.id === signal.entityId);
              if (!scenario) {
                return;
              }

              signal.updates.forEach(update => {
                const fieldName = normalizeAsoScenarioFieldName(update.fieldName);
                if (!fieldName || update.value === undefined) {
                  return;
                }

                if (fieldName === 'name') {
                  scenario.name = update.value;
                }

                if (fieldName === 'status') {
                  const parsedStatus = parseAsoScenarioStatus(update.value);
                  if (parsedStatus) {
                    scenario.status = parsedStatus;
                  }
                }
              });
            });
          };

          signalrClient.on('ProjectConfigurationChanged', listener);

          await api.cacheEntryRemoved;
          signalrClient.off('ProjectConfigurationChanged', listener);
        } catch {
          // no-op in case cache entry was removed before data was loaded
        }
      },
    }),

    createAsoScenario: builder.mutation<AsoScenarioResponse, AsoScenarioCreateRequest>({
      queryFn: async payload => {
        const { data } = await AsoCollection.scenariosCreate(payload);
        return { data: data.data };
      },
      invalidatesTags: [TAGS.list],
    }),

    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- void
    updateAsoScenario: builder.mutation<void, { id: number; payload: AsoScenarioUpdateRequest }>({
      queryFn: async ({ id, payload }) =>
        runVoid(() => AsoCollection.scenariosPartialUpdate(id, payload)),
      invalidatesTags: (result, error, { id }) => [TAGS.list, { type: TAGS.detail, id }],
    }),

    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- void
    deleteAsoScenario: builder.mutation<void, number>({
      queryFn: async id => runVoid(() => AsoCollection.scenariosDelete(id)),
      invalidatesTags: (result, error, id) => [
        TAGS.list,
        { type: TAGS.detail, id },
        { type: TAGS.scopes, id },
        { type: TAGS.layers, id },
        { type: TAGS.storeTiers, id },
        { type: TAGS.storeTierSettings, id },
        { type: TAGS.configuration, id },
        { type: TAGS.storeTierConfigurations, id },
      ],
    }),

    // Scopes
    getAsoScopes: builder.query<AsoScenarioScopeResponse[], number>({
      queryFn: async asoScenarioId => {
        const { data } = await AsoCollection.scenariosScopesList(asoScenarioId);
        return { data: data.data };
      },
      providesTags: (result, error, asoScenarioId) => [{ type: TAGS.scopes, id: asoScenarioId }],
      async onCacheEntryAdded(asoScenarioId, api) {
        await handleAsoScenarioScopeChangeListener(
          asoScenarioId,
          getSelectedProjectShortName(),
          api,
        );
      },
    }),

    createAsoScope: builder.mutation<AsoScenarioScopeResponse, { asoScenarioId: number }>({
      queryFn: async ({ asoScenarioId }) => {
        const { data } = await AsoCollection.scenariosScopesCreate(asoScenarioId);
        return { data: data.data };
      },
      invalidatesTags: (result, error, { asoScenarioId }) => [
        { type: TAGS.layers, id: asoScenarioId },
      ],
      async onQueryStarted({ asoScenarioId }, api) {
        try {
          const { data } = await api.queryFulfilled;
          api.dispatch(
            apiAso.util.updateQueryData('getAsoScopes', asoScenarioId, draft => {
              upsertAsoScope(draft, data);
            }),
          );
        } catch {
          // no-op in case mutation failed
        }
      },
    }),

    updateAsoScope: builder.mutation<
      AsoScenarioScopeResponse,
      { asoScenarioId: number; scopeId: number; payload: AsoScenarioScopeUpdateRequest }
    >({
      queryFn: async ({ asoScenarioId, scopeId, payload }) => {
        const { data } = await AsoCollection.scenariosScopesPartialUpdate(
          asoScenarioId,
          scopeId,
          payload,
        );
        return { data: data.data };
      },
      async onQueryStarted({ asoScenarioId }, api) {
        try {
          const { data } = await api.queryFulfilled;
          api.dispatch(
            apiAso.util.updateQueryData('getAsoScopes', asoScenarioId, draft => {
              upsertAsoScope(draft, data);
            }),
          );
        } catch {
          // no-op in case mutation failed
        }
      },
    }),

    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- void
    deleteAsoScope: builder.mutation<
      void,
      { asoScenarioId: number; scopeId: number; reusableScopeIds?: number[] }
    >({
      queryFn: async ({ asoScenarioId, scopeId }) =>
        runVoid(() => AsoCollection.scenariosScopesDelete(asoScenarioId, scopeId)),
      invalidatesTags: (result, error, { asoScenarioId, reusableScopeIds }) => [
        { type: TAGS.layers, id: asoScenarioId },
        ...(error ? [] : getScopeUsageTags(...(reusableScopeIds ?? []))),
      ],
      async onQueryStarted({ asoScenarioId, scopeId }, api) {
        try {
          await api.queryFulfilled;
          api.dispatch(
            apiAso.util.updateQueryData('getAsoScopes', asoScenarioId, draft => {
              removeAsoScope(draft, scopeId);
            }),
          );
        } catch {
          // no-op in case mutation failed
        }
      },
    }),

    updateAsoScopeSource: builder.mutation<
      AsoScenarioScopeSourceResponse,
      {
        asoScenarioId: number;
        scopeId: number;
        payload: AsoScenarioScopeSourceUpdateRequest;
        previousReusableScopeId?: number | null;
      }
    >({
      queryFn: async ({ asoScenarioId, scopeId, payload }) => {
        const { data } = await AsoCollection.scenariosScopesSourcePartialUpdate(
          asoScenarioId,
          scopeId,
          payload,
        );
        return { data: data.data };
      },
      invalidatesTags: (result, error, { payload, previousReusableScopeId }) =>
        error || payload.scopeId === undefined
          ? []
          : getScopeUsageTags(previousReusableScopeId, result?.scopeId),
      async onQueryStarted({ asoScenarioId, scopeId }, api) {
        try {
          const { data } = await api.queryFulfilled;
          api.dispatch(
            apiAso.util.updateQueryData('getAsoScopes', asoScenarioId, draft => {
              setAsoScopeSource(draft, scopeId, data);
            }),
          );
        } catch {
          // no-op in case mutation failed
        }
      },
    }),

    createAsoScopeTarget: builder.mutation<
      AsoScenarioScopeTargetResponse,
      { asoScenarioId: number; scopeId: number }
    >({
      queryFn: async ({ asoScenarioId, scopeId }) => {
        const { data } = await AsoCollection.scenariosScopesTargetsCreate(asoScenarioId, scopeId);
        return { data: data.data };
      },
      async onQueryStarted({ asoScenarioId, scopeId }, api) {
        try {
          const { data } = await api.queryFulfilled;
          api.dispatch(
            apiAso.util.updateQueryData('getAsoScopes', asoScenarioId, draft => {
              upsertAsoScopeTarget(draft, scopeId, data);
            }),
          );
        } catch {
          // no-op in case mutation failed
        }
      },
    }),

    updateAsoScopeTarget: builder.mutation<
      AsoScenarioScopeTargetResponse,
      {
        asoScenarioId: number;
        scopeId: number;
        targetId: number;
        payload: AsoScenarioScopeTargetUpdateRequest;
        previousReusableScopeId?: number | null;
      }
    >({
      queryFn: async ({ asoScenarioId, scopeId, targetId, payload }) => {
        const { data } = await AsoCollection.scenariosScopesTargetsPartialUpdate(
          asoScenarioId,
          scopeId,
          targetId,
          payload,
        );
        return { data: data.data };
      },
      invalidatesTags: (result, error, { payload, previousReusableScopeId }) =>
        error || payload.scopeId === undefined
          ? []
          : getScopeUsageTags(previousReusableScopeId, result?.scopeId),
      async onQueryStarted({ asoScenarioId, scopeId }, api) {
        try {
          const { data } = await api.queryFulfilled;
          api.dispatch(
            apiAso.util.updateQueryData('getAsoScopes', asoScenarioId, draft => {
              upsertAsoScopeTarget(draft, scopeId, data);
            }),
          );
        } catch {
          // no-op in case mutation failed
        }
      },
    }),

    deleteAsoScopeTarget: builder.mutation<
      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- void
      void,
      {
        asoScenarioId: number;
        scopeId: number;
        targetId: number;
        reusableScopeId?: number | null;
      }
    >({
      queryFn: async ({ asoScenarioId, scopeId, targetId }) =>
        runVoid(() => AsoCollection.scenariosScopesTargetsDelete(asoScenarioId, scopeId, targetId)),
      invalidatesTags: (result, error, { reusableScopeId }) =>
        error ? [] : getScopeUsageTags(reusableScopeId),
      async onQueryStarted({ asoScenarioId, scopeId, targetId }, api) {
        try {
          await api.queryFulfilled;
          api.dispatch(
            apiAso.util.updateQueryData('getAsoScopes', asoScenarioId, draft => {
              removeAsoScopeTarget(draft, scopeId, targetId);
            }),
          );
        } catch {
          // no-op in case mutation failed
        }
      },
    }),

    // Layers
    getAsoLayers: builder.query<AsoScenarioMinLayerResponse[], number>({
      queryFn: async asoScenarioId => {
        const { data } = await AsoCollection.scenariosMinLayersList(asoScenarioId);
        return { data: data.data };
      },
      providesTags: (result, error, asoScenarioId) => [{ type: TAGS.layers, id: asoScenarioId }],
      async onCacheEntryAdded(asoScenarioId, api) {
        await handleAsoScenarioLayerChangeListener(asoScenarioId, api);
      },
    }),

    createAsoLayer: builder.mutation<
      AsoScenarioMinLayerResponse,
      { asoScenarioId: number; payload: AsoScenarioMinLayerCreateRequest }
    >({
      queryFn: async ({ asoScenarioId, payload }) => {
        const { data } = await AsoCollection.scenariosMinLayersCreate(asoScenarioId, payload);
        return { data: data.data };
      },
    }),

    updateAsoLayer: builder.mutation<
      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- void
      void,
      { asoScenarioId: number; layerId: number; payload: AsoScenarioMinLayerUpdateRequest }
    >({
      queryFn: async ({ asoScenarioId, layerId, payload }) =>
        runVoid(() =>
          AsoCollection.scenariosMinLayersPartialUpdate(asoScenarioId, layerId, payload),
        ),
    }),

    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- void
    deleteAsoLayer: builder.mutation<void, { asoScenarioId: number; layerId: number }>({
      queryFn: async ({ asoScenarioId, layerId }) =>
        runVoid(() => AsoCollection.scenariosMinLayersDelete(asoScenarioId, layerId)),
    }),

    // Store Tier Settings
    getAsoStoreTierSettings: builder.query<AsoScenarioStoreTierSettingsResponse, number>({
      queryFn: async asoScenarioId => {
        const { data } = await AsoCollection.scenariosStoreTierSettingsList(asoScenarioId);
        return { data: data.data };
      },
      providesTags: (result, error, asoScenarioId) => [
        { type: TAGS.storeTierSettings, id: asoScenarioId },
      ],
    }),

    updateAsoStoreTierSettings: builder.mutation<
      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- void
      void,
      { asoScenarioId: number; payload: AsoScenarioStoreTierSettingsUpdateRequest }
    >({
      queryFn: async ({ asoScenarioId, payload }) =>
        runVoid(() =>
          AsoCollection.scenariosStoreTierSettingsPartialUpdate(asoScenarioId, payload),
        ),
      invalidatesTags: (result, error, { asoScenarioId }) => [
        { type: TAGS.storeTierSettings, id: asoScenarioId },
      ],
    }),

    // Store Tiers
    getAsoStoreTiers: builder.query<AsoScenarioStoreTierResponse[], number>({
      queryFn: async asoScenarioId => {
        const { data } = await AsoCollection.scenariosStoreTiersList(asoScenarioId);
        return { data: data.data };
      },
      providesTags: (result, error, asoScenarioId) => [
        { type: TAGS.storeTiers, id: asoScenarioId },
      ],
    }),

    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- void
    recomputeAsoStoreTiers: builder.mutation<void, number>({
      queryFn: async asoScenarioId =>
        runVoid(() => AsoCollection.recomputeAsoScenarioStoreTiers(asoScenarioId)),
      invalidatesTags: (result, error, asoScenarioId) => [
        { type: TAGS.storeTiers, id: asoScenarioId },
      ],
    }),

    updateAsoStoreTier: builder.mutation<
      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- void
      void,
      { asoScenarioId: number; storeId: number; payload: AsoScenarioStoreTierUpdateRequest }
    >({
      queryFn: async ({ asoScenarioId, storeId, payload }) =>
        runVoid(() =>
          AsoCollection.scenariosStoreTiersPartialUpdate(asoScenarioId, storeId, payload),
        ),
      invalidatesTags: (result, error, { asoScenarioId }) => [
        { type: TAGS.storeTiers, id: asoScenarioId },
      ],
    }),

    forceAsoStoreTiers: builder.mutation<
      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- void
      void,
      { asoScenarioId: number; payload: AsoScenarioStoreTierForceRequest }
    >({
      queryFn: async ({ asoScenarioId, payload }) =>
        runVoid(() => AsoCollection.forceAsoScenarioStoreTiers(asoScenarioId, payload)),
      invalidatesTags: (result, error, { asoScenarioId }) => [
        { type: TAGS.storeTiers, id: asoScenarioId },
      ],
    }),

    // Configuration
    getAsoConfiguration: builder.query<AsoScenarioConfiguration, number>({
      queryFn: async asoScenarioId => {
        const { data } = await AsoCollection.scenariosConfigurationList(asoScenarioId);
        return { data: data.data };
      },
      providesTags: (result, error, asoScenarioId) => [
        { type: TAGS.configuration, id: asoScenarioId },
      ],
      async onCacheEntryAdded(asoScenarioId, api) {
        try {
          await api.cacheDataLoaded;

          const listener = (signal: SignalAsoConfigurationChanged) => {
            // Only process if it's for this ASO scenario and entity type is AsoScenario
            if (signal.entityType === 'AsoScenario' && signal.entityId === asoScenarioId) {
              if (signal.operationType === ApiOperationType.Patch) {
                api.updateCachedData(draft => {
                  signal.updates.forEach((update: SignalrUpdate) => {
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- needed
                    const field = update.fieldName as keyof AsoScenarioConfiguration;
                    const value = update.value;

                    if (field in draft && value !== undefined) {
                      // Handle boolean fields (sent as "0" or "1")
                      switch (field) {
                        case 'useMinRedistributionQuantity':
                        case 'ignoreMinRedistributionQtyAndSetWhenDepletingToZero':
                          draft[field] = value === '1';
                          break;
                        case 'supplyPreferenceModifier1Supply':
                        case 'supplyPreferenceModifier2Supply':
                        case 'supplyPreferenceModifier3Supply':
                        case 'supplyPreferenceModifier1Modifier':
                        case 'supplyPreferenceModifier2Modifier':
                        case 'supplyPreferenceModifier3Modifier':
                        case 'specialPeriodMonths':
                        case 'routeValueToMerge':
                        case 'routeValueToRemove':
                        case 'maxRouteCount':
                        case 'maxPickingPositionsForOneSku': // noinspection SuspiciousTypeOfGuard -- just doublecheck
                          if (value && typeof value === 'string' && value.trim()) {
                            const parsed = parseInt(value, 10);
                            draft[field] = !isNaN(parsed) ? parsed : null;
                          } else {
                            draft[field] = null;
                          }
                          break;
                        case 'specialPeriodWeight':
                        case 'preferExistingRoute':
                        case 'preferExistingPickingPosition': // noinspection SuspiciousTypeOfGuard -- just doublecheck
                          if (value && typeof value === 'string' && value.trim()) {
                            draft[field] = parseFloat(value);
                          }
                          break;
                        case 'specialPeriodMode': // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- is enum value
                          draft[field] = value as AsoScenarioSpecialPeriodMode;
                          break;
                        case 'useSets': // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- is enum value
                          draft[field] = value as AsoScenarioUseSets;
                          break;
                        case 'additionalOneStepAdjustment': // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- is enum value
                          draft[field] = value as AsoScenarioAdditionalOneStepAdjustment;
                          break;
                        case 'specialPeriodFrom':
                        case 'specialPeriodTo':
                          draft[field] = value || null;
                          break;
                        case 'asoScenarioId':
                        case 'asoScenario':
                          throw new Error("This value shouldn't be updated");
                      }
                    }
                  });
                });
              }
            }
          };

          signalrClient.on('ProjectConfigurationChanged', listener);

          await api.cacheEntryRemoved;
          signalrClient.off('ProjectConfigurationChanged', listener);
        } catch {
          // no-op in case cache entry was removed before data was loaded
        }
      },
    }),

    updateAsoConfiguration: builder.mutation<
      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- void
      void,
      { asoScenarioId: number; payload: AsoScenarioConfigurationPatchRequest }
    >({
      queryFn: async ({ asoScenarioId, payload }) =>
        runVoid(() => AsoCollection.scenariosConfigurationPartialUpdate(asoScenarioId, payload)),
    }),

    getAsoScenarioStoreTierConfigurations: builder.query<
      AsoScenarioStoreTierConfigurationResponse[],
      number
    >({
      queryFn: async asoScenarioId => {
        const { data } = await AsoCollection.scenariosStoreTierConfigurationsList(asoScenarioId);
        return { data: data.data };
      },
      providesTags: (result, error, asoScenarioId) => [
        { type: TAGS.storeTierConfigurations, id: asoScenarioId },
      ],
      async onCacheEntryAdded(asoScenarioId, api) {
        try {
          await api.cacheDataLoaded;

          const listener = (signal: SignalAsoScenarioChanged) => {
            if (
              signal.entityType !== 'AsoScenarioStoreTierConfiguration' ||
              signal.entityId !== asoScenarioId ||
              signal.operationType !== ApiOperationType.Patch
            ) {
              return;
            }

            api.updateCachedData(draft => {
              const signalStoreTier = signal.storeTier ?? null;
              const row = draft.find(item => (item.storeTier ?? null) === signalStoreTier);

              if (!row) {
                return;
              }

              signal.updates.forEach(update => {
                applyAsoScenarioStoreTierConfigurationUpdate(row, update);
              });
            });
          };

          signalrClient.on('AsoScenarioChanged', listener);

          await api.cacheEntryRemoved;
          signalrClient.off('AsoScenarioChanged', listener);
        } catch {
          // no-op in case cache entry was removed before data was loaded
        }
      },
    }),

    updateAsoStoreTierConfiguration: builder.mutation<
      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- void
      void,
      {
        asoScenarioId: number;
        storeTier: AsoScenarioStoreTierType | null;
        payload: AsoScenarioStoreTierConfigurationPatchRequest;
      }
    >({
      queryFn: async ({ asoScenarioId, storeTier, payload }) =>
        runVoid(() =>
          AsoCollection.scenariosStoreTierConfigurationsPartialUpdate(
            asoScenarioId,
            storeTier ?? AsoScenarioStoreTierType.Default,
            payload,
          ),
        ),
    }),

    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- void
    computeAso: builder.mutation<void, CreateAsoParams>({
      queryFn: async ({ asoScenarioId, ...query }) =>
        runVoid(() => AsoCollection.computeAsoScenario(asoScenarioId, query)),
      invalidatesTags: (result, error, { asoScenarioId }) => [
        { type: TAGS.detail, id: asoScenarioId },
        { type: TAGS.layers, id: asoScenarioId },
        { type: TAGS.storeTiers, id: asoScenarioId },
      ],
    }),
  }),
});

export const {
  // Requests
  useCreateAsoRequestMutation,
  useGetAsoRequestsQuery,
  useUpdateAsoRequestMutation,
  useDeleteAsoRequestMutation,

  // ASO list/detail
  useGetAsoScenarioListQuery,
  useCreateAsoScenarioMutation,
  useUpdateAsoScenarioMutation,
  useDeleteAsoScenarioMutation,

  // Scopes
  useGetAsoScopesQuery,
  useCreateAsoScopeMutation,
  useUpdateAsoScopeMutation,
  useDeleteAsoScopeMutation,
  useUpdateAsoScopeSourceMutation,
  useCreateAsoScopeTargetMutation,
  useUpdateAsoScopeTargetMutation,
  useDeleteAsoScopeTargetMutation,

  // Layers
  useGetAsoLayersQuery,
  useCreateAsoLayerMutation,
  useUpdateAsoLayerMutation,
  useDeleteAsoLayerMutation,

  // Store tiers
  useGetAsoStoreTierSettingsQuery,
  useUpdateAsoStoreTierSettingsMutation,
  useGetAsoStoreTiersQuery,
  useRecomputeAsoStoreTiersMutation,
  useUpdateAsoStoreTierMutation,
  useForceAsoStoreTiersMutation,

  // Configuration
  useGetAsoConfigurationQuery,
  useUpdateAsoConfigurationMutation,
  useGetAsoScenarioStoreTierConfigurationsQuery,
  useUpdateAsoStoreTierConfigurationMutation,

  useComputeAsoMutation,
} = apiAso;
